import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { errorMessage } from '../../actions/ModelActions.redux';

import Scene from '../ThreeJS/Scene/Scene.container';
import MapControls from './MapControls/MapControls';
import {
  initMap,
  destroyMap,
  getMap,
  resizeMap,
  subscribeMapReady,
} from '../interface';
import { usePrevious, useRenderings } from '../common/hooks';

import './Map.scss';

const Map = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { config, settings } = useSelector((state) => state);
  const settingsCallFinished =
    Object.keys(settings).length > 1 && settings.constructor === Object;
  const defaultView = settings && settings.default_view;
  const [isMapReady, setIsMapReady] = useState(false);
  const mapContainer = useRef();
  const mapSize = location.pathname === '/' ? 'large' : 'small';
  const previousMapSize = usePrevious(mapSize);

  useEffect(() => {
    if (isMapReady && previousMapSize && mapSize !== previousMapSize) {
      resizeMap();
    }
  }, [isMapReady, mapSize, previousMapSize]);

  useRenderings({ isMapReady });

  useEffect(() => {
    let cancelled = false;
    let unsubscribeMapReady = () => {};

    const createHost = async () => {
      const { mapTileStyle, centerLat, centerLng, centerZoom } = config;
      let bearingVal;
      let latVal;
      let lngVal;
      let pitchVal;
      let zoomVal;
      if (defaultView && typeof defaultView === 'object') {
        const { bearing, lat, lng, pitch, zoom } = defaultView;
        bearingVal = bearing;
        lngVal = lng;
        latVal = lat;
        pitchVal = pitch;
        zoomVal = zoom;
      }
      const created = await initMap({
        bearing: bearingVal ? bearingVal : 0,
        container: mapContainer.current,
        lngLat: latVal && lngVal ? [lngVal, latVal] : [centerLng, centerLat],
        pitch: pitchVal ? pitchVal : 45,
        style: mapTileStyle,
        zoom: zoomVal ? zoomVal : centerZoom,
        enableTerrain: Boolean(config.isTerrainEnabled),
      });
      if (cancelled) {
        if (created && getMap() === created) {
          await destroyMap();
        }
        return;
      }
      unsubscribeMapReady = subscribeMapReady(() => {
        if (!cancelled) {
          setIsMapReady(true);
          resizeMap();
        }
      });
    };

    if (
      !isMapReady &&
      mapContainer.current &&
      config.map &&
      settingsCallFinished
    ) {
      createHost().catch((error) => {
        if (!cancelled) {
          dispatch(errorMessage(error?.message));
        }
      });
    }

    return () => {
      cancelled = true;
      unsubscribeMapReady();
    };
  }, [
    config,
    dispatch,
    isMapReady,
    settingsCallFinished,
    defaultView,
    settings,
    location.pathname,
  ]);

  useEffect(() => {
    return () => {
      destroyMap();
    };
  }, []);

  return (
    <>
      <div id="map" className={`map ${mapSize}`} ref={mapContainer}>
        {isMapReady && <MapControls />}
      </div>
      {isMapReady && <Scene id="3d-maps" />}
    </>
  );
};

export default Map;
